import type { PricebookCategoryRow } from '@/types/pricebook';

/**
 * Between folders, the way a file path reads: `HVAC / Equipment / Filters`.
 * Everything that renders a category path shares it, so a path split in one
 * place can always be rejoined in another.
 */
export const CATEGORY_PATH_SEPARATOR = ' / ';

/**
 * Breadcrumb path per category, e.g. `HVAC / Filters`.
 *
 * One tree covers the whole catalog, so the same leaf name legitimately appears
 * under several parents — "Filters" under HVAC and under Plumbing. Rendering or
 * searching the leaf name alone leaves those indistinguishable. Indenting with
 * spaces doesn't help either: HTML collapses them, so a `<Select>` option shows
 * no indentation at all.
 *
 * The flat list arrives depth-annotated in tree order, so a single pass with a
 * stack indexed by depth reconstructs every ancestor chain.
 */
export function buildCategoryPaths(
  categories: Pick<PricebookCategoryRow, 'id' | 'name' | 'depth'>[],
): Map<string, string> {
  const paths = new Map<string, string>();
  const ancestors: string[] = [];
  for (const category of categories) {
    // Truncating to `depth` drops the previous branch's deeper entries, so a
    // sibling never inherits a cousin's ancestors.
    ancestors.length = category.depth;
    ancestors[category.depth] = category.name;
    // An orphan — a row deeper than anything before it, because its parent was
    // filtered out by scoping or deleted between fetches — leaves holes in the
    // stack. Dropping them degrades the path to the names actually known
    // instead of rendering ` /  / Filters`.
    paths.set(category.id, ancestors.filter(Boolean).join(CATEGORY_PATH_SEPARATOR));
  }
  return paths;
}

/**
 * A path split into the part that says WHERE the folder sits and the folder
 * itself, so the leaf can be emphasised over its ancestors. A root folder has
 * no ancestors — never a leading separator.
 */
export function splitCategoryPath(path: string): { ancestors: string; leaf: string } {
  const segments = path.split(CATEGORY_PATH_SEPARATOR);
  const leaf = segments[segments.length - 1] ?? path;
  return { ancestors: segments.slice(0, -1).join(CATEGORY_PATH_SEPARATOR), leaf };
}

/**
 * Front-truncated for surfaces with no room for a deep path — a chip, a table
 * cell: `… / Equipment / Filters`.
 *
 * The FRONT goes, never the leaf: the last folder is the most identifying part
 * and the one being named. Callers pair this with the full path in a `title`,
 * so nothing is actually lost.
 */
export function truncateCategoryPath(path: string, maxSegments = 2): string {
  const segments = path.split(CATEGORY_PATH_SEPARATOR);
  if (segments.length <= maxSegments) return path;
  return ['…', ...segments.slice(-maxSegments)].join(CATEGORY_PATH_SEPARATOR);
}
